import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { CourseTableType } from 'components/CoursesTable/CoursesTableTeacher';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useMemo } from 'react';

const UseConvertToCourseTableTypeFieldsDocument = graphql(`
  fragment UseConvertToCourseTableTypeFields on Query {
    groups(
      where: {
        type: course
        organization: { uuid_IN: $organizationUuids }
        AND: [
          { OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }] }
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] }
        ]
      }
    ) {
      uuid
      __typename
      name
      managedBy
      isCommunity
      organization {
        uuid
      }
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            enabled
            lastSync
          }
        }
      }
      externalServices {
        threema {
          __typename
          enabled
          lastSync
        }
      }
      events {
        uuid
        __typename
        virtualLocations {
          uuid
          __typename
          running
        }
      }
      admins {
        uuid
        __typename
        displayNameShort
      }
      teachingUnitsConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
            assignments {
              uuid
              __typename
              submissions(where: { status: New }) {
                uuid
                __typename
              }
            }
          }
        }
      }
    }
  }
`);

export const useConvertToCourseTableType = (
  query: FragmentType<typeof UseConvertToCourseTableTypeFieldsDocument> | undefined,
) => {
  const fragment = getFragmentData(UseConvertToCourseTableTypeFieldsDocument, query);
  const groups = fragment?.groups;
  const assignments = groups?.flatMap((g) => g.teachingUnitsConnection.edges.flatMap((e) => e.node.assignments));

  const { rooms } = useMatrixClient();

  const courseNotifications = useMemo(() => {
    const notifications: Record<string, boolean> = {};

    groups?.forEach((course) => {
      const notificationRoom = rooms?.find((room) => room.name.includes(course.uuid));
      let newMessages = false;

      if (notificationRoom?.getRoomUnreadNotificationCount) {
        newMessages = notificationRoom.getRoomUnreadNotificationCount() > 0;
      }

      if (!newMessages && assignments) {
        assignments.some((assignment) => {
          return assignment.submissions.some((submission) => {
            const room = rooms?.find((room) => room?.name?.includes(submission.uuid));
            return (room?.getRoomUnreadNotificationCount() ?? 0) > 0;
          });
        });
      }

      notifications[course.uuid] = newMessages;
    });

    return notifications;
  }, [groups, rooms, assignments]);

  const data: CourseTableType[] | undefined = useMemo(() => {
    return groups?.map((course) => {
      const nextCloud = course.nextcloudConnection?.edges[0]?.properties;
      return {
        uuid: course.uuid,
        organizationUuid: course.organization.uuid,
        name: course.name,
        meetingInProgress: course.events.some((e) => e.virtualLocations.some((v) => v.running)),
        count: 0,
        newMessage: courseNotifications[course.uuid],
        community: course.isCommunity,
        source:
          course.managedBy !== 'bp'
            ? course.managedBy
            : course.admins.map((admin) => admin.displayNameShort).toString(),
        nextcloud: {
          enabled: nextCloud?.enabled ?? false,
          lastSync: nextCloud?.lastSync ? dayjs(nextCloud?.lastSync) : undefined,
        },
        threema: {
          enabled: course.externalServices?.threema?.enabled ?? false,
          lastSync: course.externalServices?.threema?.lastSync
            ? dayjs(course.externalServices?.threema.lastSync)
            : undefined,
        },
      };
    });
  }, [groups, courseNotifications]);

  const dataWithSubmissionCount = data?.map((course) => {
    const courseWithNewSubmissions = groups
      ?.map((a) => {
        const todoCount = a.teachingUnitsConnection.edges.flatMap((e) => {
          const newSubmsns = e.node.assignments.map((a) => a.submissions.length);
          return newSubmsns.reduce((a, b) => a + b, 0);
        });
        return { courseUuid: a.uuid, count: todoCount.reduce((a, b) => a + b, 0) };
      })
      .find((g) => g.courseUuid === course.uuid);
    return { ...course, count: courseWithNewSubmissions?.count ?? 0 };
  });

  return dataWithSubmissionCount ?? [];
};
